import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Modal } from '@/components';
import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';
import { cn } from '@/design-system/lib/utils';
import { SearchSelect } from '@/design-system/search-select';
import { toast } from '@/design-system/toaster';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/design-system/table';

import { fmtDateTime, fmtDistance, fmtQty } from '../format';
import { useBulkPicking } from '../hooks/useBulkPicking';
import { canEditOutbound, GOODS_ISSUE_STATUS } from '../status';
import { type GoodsIssueSummary, type OutboundOrder } from '../types';
import {
  type BulkPickingOutcome,
  chipLabel,
  firstIssue,
  groupRows,
  isItemDirty,
  type ItemDraft,
  type ItemState,
  knowledgeOf,
  optionsFor,
  rowLabel,
  type SaveKind,
  savePlan,
  STATE_LABEL,
  suggestPickers,
  tally,
} from './bulkPicking';
import { DisabledReason } from './dialogs';
import OutboundRequestEditModal from './OutboundRequestEditModal';
import { FormField, TextInput } from './form';
import { type PickerA11yProps, usePickerA11y } from './pickerA11y';
import {
  checkRows,
  MAX_EDIT_ROWS,
  type RowErrorKey,
  type RowField,
  withStock,
  withStorage,
} from './pickingEdit';
import { codeCls, LoadError, MappedBadge, metaCls, nameCls } from './ui';

/**
 * "Edit picking" for the pickings selected on the Picking list: every picking
 * is a card in one scrolling list (no paging through them), each saved on its
 * own request with the version it was loaded with; a card loads when it comes
 * near the view. Per picking: who picks (picker), and the picked lines as rows grouped
 * by SKU — each row one candidate stock row (location + batch), a qty — whose
 * per-SKU totals must equal the order. "Simpan saja" confirms the pick,
 * "Simpan & siap diambil" sends it on to Shipping. A picking that changed under
 * us (409) is marked and reloaded alone; the others are untouched.
 */

interface Props {
  /** The selected pickings, in list order (read once, when it opens). */
  items: GoodsIssueSummary[];
  /** Closed (X / Esc / Selesai): how each picking ended. */
  onDone: (outcomes: BulkPickingOutcome[]) => void;
}

/* --------------------------------- chips ---------------------------------- */

const CHIP_TONE: Record<ItemState, string> = {
  pending: 'bg-bg-white-0 text-text-sub-600 hover:bg-bg-weak-50',
  saved:
    'bg-(--state-success-lighter) text-(--state-success-dark) hover:bg-(--state-success-light)',
  ready:
    'bg-(--state-success-lighter) text-(--state-success-dark) hover:bg-(--state-success-light)',
  stale:
    'bg-(--state-warning-lighter) text-(--state-warning-dark) hover:bg-(--state-warning-light)',
  failed:
    'bg-(--state-error-lighter) text-(--state-error-dark) hover:bg-(--state-error-light)',
};
const CHIP_BORDER: Record<ItemState, string> = {
  pending: 'border-stroke-soft-200',
  saved: 'border-(--state-success-light)',
  ready: 'border-(--state-success-light)',
  stale: 'border-(--state-warning-light)',
  failed: 'border-(--state-error-light)',
};
const CHIP_ICON: Partial<Record<ItemState, string>> = {
  saved: 'ri-check-line',
  ready: 'ri-check-double-line',
  stale: 'ri-alert-line',
  failed: 'ri-close-circle-line',
};

/** A picking's short code at the top; clicking scrolls to its card. */
const Chip: React.FC<{
  item: GoodsIssueSummary;
  state: ItemState;
  dirty: boolean;
  onClick: () => void;
}> = ({ item, state, dirty, onClick }) => (
  <button
    type="button"
    title={`${item.code} · ${STATE_LABEL[state]}`}
    onClick={onClick}
    data-testid={`bulk-picking-chip-${item.id}`}
    data-state={state}
    className={cn(
      'inline-flex items-center gap-1 rounded-md border px-2 py-1 font-mono text-[11px] tabular-nums transition-colors',
      CHIP_TONE[state],
      CHIP_BORDER[state]
    )}
  >
    {CHIP_ICON[state] && (
      <i className={cn(CHIP_ICON[state], 'text-[12px]')} aria-hidden="true" />
    )}
    {chipLabel(item.code)}
    {dirty && (
      <span
        className="size-1.5 rounded-full bg-(--state-warning-base)"
        role="img"
        aria-label="ada perubahan belum disimpan"
      />
    )}
  </button>
);

/* ------------------------------ small pieces ------------------------------ */

const FieldMessage: React.FC<{ message?: string | null }> = ({ message }) =>
  message ? (
    <p className="mt-1 text-[11px] leading-snug text-(--state-error-base)">
      {message}
    </p>
  ) : null;

const InfoItem: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex min-w-0 flex-col gap-0.5">
    <span className={metaCls}>{label}</span>
    <span className={cn(nameCls, 'truncate')}>{children}</span>
  </div>
);

/** One stock row (location + batch) of the SKU, picked from a local list. */
const CandidateSelect: React.FC<
  PickerA11yProps & {
    options: { value: string; label: string }[];
    value: string;
    selectedLabel?: string;
    placeholder: string;
    disabled?: boolean;
    invalid?: boolean;
    onPick: (stockID: string) => void;
  }
> = ({
  options,
  value,
  selectedLabel,
  placeholder,
  disabled,
  invalid,
  onPick,
  'aria-label': ariaLabel,
}) => {
  const ref = usePickerA11y(ariaLabel, invalid);
  return (
    <div ref={ref} className="min-w-0">
      <SearchSelect
        options={options}
        value={value}
        selectedLabelFallback={selectedLabel}
        onChange={(v) => {
          if (v) onPick(v);
        }}
        placeholder={placeholder}
        allowClear={false}
        disabled={disabled}
        error={invalid}
        emptyMessage="Tidak ada lokasi dengan stok untuk SKU ini"
      />
    </div>
  );
};

type Bulk = ReturnType<typeof useBulkPicking>;

/* ------------------------------ item editor ------------------------------- */

const ItemEditor: React.FC<{
  bp: Bulk;
  id: string;
  draft: ItemDraft;
  suggestions: string[];
  onSave: (id: string, kind: SaveKind) => void;
}> = ({ bp, id, draft, suggestions, onSave }) => {
  const detail = draft.detail;
  const saving = bp.busy?.id === id;
  const locked = draft.state === 'ready' || draft.state === 'stale' || saving;
  const known = useMemo(() => knowledgeOf(draft), [draft]);
  const check = useMemo(
    () => checkRows(draft.rows, draft.ordered, known),
    [draft.rows, draft.ordered, known]
  );
  const groups = useMemo(
    () =>
      groupRows(
        draft.rows,
        draft.ordered.map((o) => o.sku)
      ),
    [draft.rows, draft.ordered]
  );
  const orderedBy = new Map(draft.ordered.map((o) => [o.sku, o]));
  const totalOf = new Map(check.totals.map((t) => [t.sku, t]));
  const errorOf = (index: number, key: number, field: RowErrorKey) =>
    draft.serverErrors.get(key)?.[field] ??
    (field === 'row' ? null : check.rows[index]?.errors[field as RowField]) ??
    null;

  if (!detail) return null;
  const status = detail.goodsIssue.status;
  const issue = firstIssue(check, draft.rows);
  const blocked =
    draft.state === 'stale' ? 'Muat ulang dulu untuk menyimpan.' : issue;
  const nothing = savePlan('save', draft, status).nothing;
  const mismatched = check.totals.filter((t) => t.diff !== 0);

  return (
    <>
      <FormField
        label="Picker"
        hint="Orang yang mengambil barang. Disimpan bersama picking ini."
        error={draft.pickerError}
      >
        <TextInput
          data-testid="bulk-picking-picker"
          value={draft.picker}
          placeholder="Nama picker"
          invalid={!!draft.pickerError}
          disabled={locked}
          onChange={(v) => bp.setPicker(id, v)}
        />
      </FormField>
      {suggestions.length > 0 && !locked && (
        <div className="-mt-2 flex flex-wrap items-center gap-1.5">
          <span className={metaCls}>Sering dipakai</span>
          {suggestions.map((name) => (
            <button
              key={name}
              type="button"
              data-testid="bulk-picking-name"
              onClick={() => bp.setPicker(id, name)}
              className="rounded-full border border-stroke-soft-200 bg-bg-white-0 px-2 py-0.5 text-[11px] text-text-sub-600 transition-colors hover:bg-bg-weak-50 hover:text-text-strong-950"
            >
              {name}
            </button>
          ))}
        </div>
      )}

      {draft.state === 'ready' ? (
        <Alert
          status="success"
          appearance="stroke"
          size="sm"
          data-testid="bulk-picking-ready"
        >
          Picking ini sudah siap diambil dan masuk Shipping.
        </Alert>
      ) : (
        <>
          {mismatched.length > 0 && (
            <Alert
              status="warning"
              appearance="stroke"
              size="lg"
              title="Total per SKU belum sesuai order"
              data-testid="bulk-picking-totals"
            >
              <ul className="flex flex-col gap-0.5">
                {mismatched.map((t) => (
                  <li
                    key={t.sku}
                    className="font-mono text-[12px] tabular-nums"
                  >
                    {t.sku}: {fmtQty(t.total)} / {fmtQty(t.ordered)}
                  </li>
                ))}
              </ul>
            </Alert>
          )}
          {draft.formErrors.length > 0 && (
            <Alert
              status="error"
              appearance="stroke"
              size="sm"
              data-testid="bulk-picking-form-errors"
            >
              {draft.formErrors.join(' ')}
            </Alert>
          )}
          <div className="overflow-hidden rounded-lg border border-stroke-soft-200 bg-bg-white-0">
            <Table containerClassName="overflow-x-auto">
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[150px] text-[11px]">
                    SKU
                  </TableHead>
                  <TableHead className="min-w-[280px] text-[11px]">
                    Item location
                  </TableHead>
                  <TableHead className="w-[130px] text-right text-[11px]">
                    Qty
                  </TableHead>
                  <TableHead className="w-px">
                    <span className="sr-only">Hapus</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groups.map((g) => {
                  const info = orderedBy.get(g.sku);
                  const total = totalOf.get(g.sku);
                  return g.items.map(({ row, index }, k) => {
                    const n = index + 1;
                    const slot = draft.sources[row.sku];
                    const cand = row.stockID
                      ? known.candidates.get(row.stockID)
                      : undefined;
                    const stockErr =
                      errorOf(index, row.key, 'stockID') ??
                      errorOf(index, row.key, 'storageID');
                    const qtyErr = errorOf(index, row.key, 'qty');
                    const rowErr =
                      errorOf(index, row.key, 'row') ??
                      errorOf(index, row.key, 'sku');
                    return (
                      <TableRow
                        key={row.key}
                        className={cn(
                          'align-top hover:bg-transparent',
                          k === 0 && 'border-t border-stroke-soft-200'
                        )}
                        data-testid={`bulk-picking-row-${index}`}
                      >
                        <TableCell className="px-3 py-2">
                          {k === 0 && (
                            <div className="flex flex-col items-start gap-0.5">
                              <span className={codeCls}>{g.sku}</span>
                              {info && (
                                <span className={metaCls}>{info.itemName}</span>
                              )}
                              {total && (
                                <span
                                  className={cn(
                                    'font-mono text-[11px] tabular-nums',
                                    total.diff === 0
                                      ? 'text-text-sub-600'
                                      : 'text-(--state-error-base)'
                                  )}
                                  data-testid={`bulk-picking-total-${g.sku}`}
                                  data-state={total.diff === 0 ? 'ok' : 'off'}
                                >
                                  {fmtQty(total.total)} /{' '}
                                  {fmtQty(total.ordered)} {info?.uomName}{' '}
                                  {total.diff === 0 && (
                                    <i
                                      className="ri-check-line text-(--state-success-base)"
                                      aria-label="sesuai"
                                    />
                                  )}
                                </span>
                              )}
                              <Button
                                tone="primary"
                                style="link"
                                size="xs"
                                className="h-auto px-0 py-0 text-[11px]"
                                data-testid={`bulk-picking-add-${g.sku}`}
                                disabled={
                                  locked || draft.rows.length >= MAX_EDIT_ROWS
                                }
                                onClick={() =>
                                  bp.addLocation(
                                    id,
                                    g.sku,
                                    total ? -total.diff : 0
                                  )
                                }
                              >
                                + Tambah lokasi
                              </Button>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="px-3 py-2">
                          <CandidateSelect
                            aria-label={`Lokasi ${row.sku} baris ${n}`}
                            options={optionsFor(draft, row)}
                            value={row.stockID}
                            selectedLabel={rowLabel(row, known)}
                            placeholder={
                              slot ? 'Pilih lokasi & batch' : 'Memuat lokasi…'
                            }
                            invalid={!!stockErr}
                            disabled={locked || slot?.status !== 'ready'}
                            onPick={(stockID) => {
                              const c = known.candidates.get(stockID);
                              if (!c) return;
                              bp.updateRow(id, row.key, (r) =>
                                withStock(
                                  withStorage(r, c.storageID),
                                  c.stockID
                                )
                              );
                            }}
                          />
                          {slot?.status === 'error' && (
                            <div className="mt-1 flex flex-wrap items-center gap-1">
                              <span className="text-[11px] text-(--state-error-base)">
                                {slot.message}
                              </span>
                              <Button
                                tone="neutral"
                                style="link"
                                size="xs"
                                className="h-auto px-0 py-0 text-[11px]"
                                onClick={() => bp.retrySources(id, row.sku)}
                              >
                                Coba lagi
                              </Button>
                            </div>
                          )}
                          <FieldMessage message={stockErr} />
                          <FieldMessage message={rowErr} />
                        </TableCell>
                        <TableCell className="px-3 py-2">
                          <TextInput
                            aria-label={`Qty ${row.sku} baris ${n}`}
                            data-testid="bulk-picking-qty"
                            size="sm"
                            inputMode="decimal"
                            value={row.qty}
                            invalid={!!qtyErr}
                            disabled={locked}
                            className="text-right"
                            onChange={(v) =>
                              bp.updateRow(id, row.key, (r) =>
                                r.qty === v ? r : { ...r, qty: v }
                              )
                            }
                          />
                          <p className={cn(metaCls, 'mt-1 text-right')}>
                            {cand
                              ? `${fmtQty(cand.available)} tersedia di lokasi ini`
                              : 'pilih lokasi yang masih punya stok'}
                          </p>
                          <FieldMessage message={qtyErr} />
                        </TableCell>
                        <TableCell className="px-2 py-2">
                          {g.items.length > 1 && (
                            <Button
                              tone="neutral"
                              style="ghost"
                              size="icon-xs"
                              aria-label={`Hapus baris ${n}`}
                              title="Hapus baris"
                              disabled={locked}
                              onClick={() => bp.removeRow(id, row.key)}
                            >
                              <i className="ri-delete-bin-line text-[14px]" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  });
                })}
              </TableBody>
            </Table>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {(blocked || nothing) && !locked && (
              <DisabledReason testId="bulk-picking-reason">
                {blocked ?? 'Simpan saja: belum ada perubahan.'}
              </DisabledReason>
            )}
            <Button
              tone="neutral"
              style="stroke"
              size="sm"
              data-testid="bulk-picking-save"
              loading={saving && bp.busy?.kind === 'save'}
              disabled={bp.busy !== null || !!blocked || nothing}
              onClick={() => onSave(id, 'save')}
            >
              Simpan saja
            </Button>
            <Button
              size="sm"
              data-testid="bulk-picking-save-ready"
              loading={saving && bp.busy?.kind === 'ready'}
              disabled={bp.busy !== null || !!blocked}
              onClick={() => onSave(id, 'ready')}
            >
              Simpan & siap diambil
            </Button>
          </div>
        </>
      )}
    </>
  );
};

/* -------------------------------- item card ------------------------------- */

const OUTCOME_TONE: Record<ItemState, string> = {
  pending: 'text-text-sub-600',
  saved: 'text-(--state-success-dark)',
  ready: 'text-(--state-success-dark)',
  stale: 'text-(--state-warning-dark)',
  failed: 'text-(--state-error-base)',
};

const cardId = (id: string) => `bulk-picking-card-${id}`;

/** Calls `onNear` once, when the element comes near the view (or at once without IntersectionObserver). */
const useWhenNear = <T extends HTMLElement>(onNear: () => void) => {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      onNear();
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          onNear();
          io.disconnect();
        }
      },
      { rootMargin: '400px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [onNear]);
  return ref;
};

const ItemCard: React.FC<{
  bp: Bulk;
  item: GoodsIssueSummary;
  position: number;
  suggestions: string[];
  onSave: (id: string, kind: SaveKind) => void;
  /** "Ubah" penerima, alamat & jarak of the order behind this picking. */
  onEditOrder: (id: string, order: OutboundOrder) => void;
}> = ({ bp, item, position, suggestions, onSave, onEditOrder }) => {
  const { activate } = bp;
  const near = useCallback(() => activate(item.id), [activate, item.id]);
  const ref = useWhenNear<HTMLElement>(near);
  const draft = bp.drafts[item.id];
  const state: ItemState = draft?.state ?? 'pending';
  const dirty = state === 'pending' && !!draft && isItemDirty(draft);
  const detail = draft?.detail ?? null;
  const gi = detail?.goodsIssue;
  const order = detail?.outboundOrder;
  return (
    <section
      ref={ref}
      id={cardId(item.id)}
      className={cn(
        'flex scroll-mt-16 flex-col gap-4 rounded-xl border bg-bg-white-0 p-4',
        state === 'ready' || state === 'saved'
          ? 'border-(--state-success-light)'
          : state === 'stale'
            ? 'border-(--state-warning-light)'
            : state === 'failed'
              ? 'border-(--state-error-light)'
              : 'border-stroke-soft-200'
      )}
      data-testid={`bulk-picking-item-${item.id}`}
      data-state={state}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex min-w-0 items-baseline gap-2">
          <span className={metaCls}>
            {position}.
          </span>
          <span className={codeCls}>{item.code}</span>
        </div>
        <span
          className={cn('text-[12px] font-medium', OUTCOME_TONE[state])}
          data-testid={`bulk-picking-state-${item.id}`}
        >
          {dirty ? 'Ada perubahan belum disimpan' : STATE_LABEL[state]}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 rounded-lg border border-stroke-soft-200 bg-bg-weak-50 px-3 py-2.5 sm:grid-cols-4">
        <InfoItem label="Request ID">
          {order?.code ?? item.outboundOrder?.code ?? item.reference}
        </InfoItem>
        <InfoItem label="Customer">
          {order?.recipientName ?? item.outboundOrder?.recipientName ?? '-'}
        </InfoItem>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className={metaCls}>Status</span>
          <span>
            <MappedBadge
              map={GOODS_ISSUE_STATUS}
              value={gi?.status ?? item.status}
            />
          </span>
        </div>
        <InfoItem label="Versi dimuat">
          {gi ? fmtDateTime(gi.updatedAt) : '—'}
        </InfoItem>
      </div>

      {order && (
        <div
          className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-stroke-soft-200 px-3 py-2.5"
          data-testid={`bulk-picking-destination-${item.id}`}
        >
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className={metaCls}>Tujuan</span>
            <span className="text-[13px] break-words text-text-strong-950">
              {order.destinationAddress}
            </span>
            <span className={metaCls}>
              {order.recipientName} · {order.recipientPhone}
              {order.destinationNotes ? ` · ${order.destinationNotes}` : ''}
            </span>
          </div>
          <div className="flex shrink-0 items-start gap-3">
            <div className="flex flex-col items-end gap-0.5">
              <span className={metaCls}>Jarak dari hub</span>
              <span
                className={
                  order.distance == null
                    ? 'text-[12px] text-text-soft-400'
                    : 'text-[13px] font-semibold text-text-strong-950 tabular-nums'
                }
                data-testid={`bulk-picking-distance-${item.id}`}
              >
                {order.distance == null
                  ? 'Saat dispatch'
                  : fmtDistance(order.distance)}
              </span>
            </div>
            {canEditOutbound(order) && (
              <Button
                tone="neutral"
                style="stroke"
                size="xs"
                data-testid={`bulk-picking-edit-destination-${item.id}`}
                disabled={bp.busy !== null}
                onClick={() => onEditOrder(item.id, order)}
                leftIcon={<i className="ri-edit-line text-[13px]" />}
              >
                Ubah
              </Button>
            )}
          </div>
        </div>
      )}

      {state === 'stale' && (
        <Alert
          status="warning"
          appearance="stroke"
          size="lg"
          title="Picking ini berubah sejak dimuat."
          data-testid="bulk-picking-stale"
          action={
            <Button
              tone="neutral"
              style="stroke"
              size="xs"
              data-testid="bulk-picking-reload"
              disabled={bp.busy !== null}
              onClick={() => bp.reload(item.id)}
            >
              Muat ulang
            </Button>
          }
        >
          Perubahanmu di picking ini tidak disimpan. Muat ulang untuk melihat
          versi terbaru.
        </Alert>
      )}
      {state === 'failed' && (
        <Alert
          status="error"
          appearance="stroke"
          size="lg"
          title="Picking ini gagal disimpan"
          data-testid="bulk-picking-failed"
        >
          {draft?.message}
        </Alert>
      )}

      {!draft || (!detail && !draft.loadError) ? (
        <div
          className="flex flex-col gap-2"
          data-testid="bulk-picking-loading"
          aria-busy="true"
        >
          <span className={metaCls}>Memuat…</span>
          <div className="h-9 animate-pulse rounded-lg bg-bg-weak-50" />
          <div className="h-24 animate-pulse rounded-lg bg-bg-weak-50" />
        </div>
      ) : draft.loadError && !detail ? (
        <LoadError
          message={draft.loadError}
          onRetry={() => bp.retryLoad(item.id)}
        />
      ) : (
        <ItemEditor
          bp={bp}
          id={item.id}
          draft={draft}
          suggestions={suggestions}
          onSave={onSave}
        />
      )}
    </section>
  );
};

/* --------------------------------- modal ---------------------------------- */

const scrollToCard = (id: string) => {
  document
    .getElementById(cardId(id))
    ?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
};

const BulkPickingModal: React.FC<Props> = ({ items, onDone }) => {
  const bp = useBulkPicking(items);
  const [editing, setEditing] = useState<{
    id: string;
    order: OutboundOrder;
  } | null>(null);
  const counts = tally(bp.states);
  const done = counts.saved + counts.ready;

  const suggestions = useMemo(
    () =>
      suggestPickers([
        ...bp.usedPickers,
        ...items.map(
          (g) => bp.drafts[g.id]?.detail?.goodsIssue.pickerName ?? g.pickerName
        ),
      ]),
    [bp.usedPickers, bp.drafts, items]
  );

  const finish = () => onDone(bp.outcomes());

  const { save, nextPendingAfter } = bp;
  /** Save one card, then bring the next one still to do into view. */
  const onSave = useCallback(
    (id: string, kind: SaveKind) => {
      void save(id, kind).then((state) => {
        if (!state) return;
        const next = nextPendingAfter(id);
        if (next) scrollToCard(next);
      });
    },
    [save, nextPendingAfter]
  );

  const progress = [
    `${done} dari ${items.length} selesai`,
    counts.stale ? `${counts.stale} berubah` : null,
    counts.failed ? `${counts.failed} gagal` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Modal
      isOpen
      onClose={finish}
      icon="ri-pencil-line"
      title="Edit picking"
      subtitle={`${items.length} picking · scroll untuk isi semua; yang siap diambil langsung masuk Shipping`}
      width="lg"
      className="w-[820px] max-w-full"
      confirmOnClose={bp.anyDirty}
      closeOnBackdropClick={bp.busy === null}
      closeOnEscape={bp.busy === null}
      showCloseButton={bp.busy === null}
      footer={
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-col">
            <span
              className="text-[13px] font-medium text-text-strong-950"
              data-testid="bulk-picking-progress"
            >
              {progress}
            </span>
            <span className={metaCls}>
              Yang belum selesai tetap terpilih di list.
            </span>
          </div>
          <Button
            data-testid="bulk-picking-done"
            disabled={bp.busy !== null}
            onClick={finish}
          >
            Selesai
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4" data-testid="bulk-picking-modal">
        <div className="sticky top-0 z-10 -mx-5 -mt-4 flex flex-wrap gap-1.5 border-b border-stroke-soft-200 bg-bg-white-0/95 px-5 py-2.5 backdrop-blur">
          {items.map((g, i) => {
            const d = bp.drafts[g.id];
            const state = bp.states[i];
            return (
              <Chip
                key={g.id}
                item={g}
                state={state}
                dirty={state === 'pending' && !!d && isItemDirty(d)}
                onClick={() => scrollToCard(g.id)}
              />
            );
          })}
        </div>
        {items.map((g, i) => (
          <ItemCard
            key={g.id}
            bp={bp}
            item={g}
            position={i + 1}
            suggestions={suggestions}
            onSave={onSave}
            onEditOrder={(id, order) => setEditing({ id, order })}
          />
        ))}
      </div>
      <OutboundRequestEditModal
        order={editing?.order ?? null}
        onClose={() => setEditing(null)}
        onSaved={(saved) => {
          if (editing) bp.updateOrder(editing.id, saved);
          setEditing(null);
          toast.success('Penerima & alamat diubah', {
            description: `${saved.code} · jarak ${fmtDistance(saved.distance)}`,
          });
        }}
      />
    </Modal>
  );
};

export default BulkPickingModal;
